import {
  MessageCallReceived,
  HistoryResponse,
  isMessageNotReactionOrNotification,
  useIM,
  HistoryMessageItem,
  PServed,
  MessageMarkedAsRead,
  MessageDeletedByOtherEvent,
  MessageUpdatedByOtherEvent,
  MessageUpdatedByYouEvent,
  MessageDeletedByYouEvent,
  MessageStatusType,
  BaseEvent,
  GroupStatusEvent,
  BaseByOtherEvent,
  isCallMessage,
  isMessageDeleted,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  isTextMessage,
  isCallPastMessage,
  MessageCallAppointmentStart,
  MessageCallAppointmentEnd,
  MessageCallRecordCompleted,
  RequestChatHistoryPayload,
  IMQuote,
} from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch, triggerRef, shallowRef } from 'vue'

import { useNotificationItemStore } from '@/app/store/notifications/items'
import { useStoreLastReadEventIds } from '@/app/store/pinia/lastReadEventIds/lastReadEventIds'
import { useStoreRooms } from '@/app/store/pinia/rooms/rooms'
import { useGuestAuth } from '@/composables/useGuestAuth'

import { useStoreContacts } from '@/entities/contact'
import { useReactionsStore } from '@/entities/reaction'
import { useRoomScheduleStore } from '@/entities/schedule'

import { useAppBadge } from '@/shared/composables/browser-api'
import { useChatHistoryLoading } from '@/shared/composables/chat-handlers'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useIMStore } from '@/shared/store/im'
import { Contact } from '@/shared/types/api'
import { createPrivateRoom } from '@/shared/utils/im'
import { insertAtStore } from '@/shared/utils/insert-at-store'
import { yieldToMainThread } from '@/shared/utils/main-thread'
import { updateStatusIcon } from '@/shared/utils/tauri'
import { fromPServedUserIfRoom, pServedIsRoom } from '@/shared/utils/uri'

interface EventsByRoomGuid {
  [roomPServed: PServed]: Array<HistoryMessageItem>
}

type Rank = HistoryMessageItem['rank']

interface RankByEventId {
  [eventId: HistoryMessageItem['eventId']]: Rank
}

interface EventByRoomPServedAndRank {
  [roomPServed: PServed]: {
    [rank: Rank]: HistoryMessageItem
  }
}

const REQUEST_HISTORY_MESSAGES_LIMIT = 25

export const useChatMessageStore = defineStore('chat-messages', () => {
  const {
    onEditMessage,
    requestDialogs,
    requestChatHistory,
    markAsRead,
    onDeleteMessage,
    onHistory,
    onMarkAsRead,
    onConferenceCreated,
    onConferenceEdited,
    onMessage,
    onPinMessage,
    onUnpinMessage,
  } = useIM()

  const eventsByRoomPServed = shallowRef<EventsByRoomGuid>({})
  const roomIdByEventId = shallowRef(new Map<HistoryMessageItem['eventId'], PServed>())
  const rankByEventId = shallowRef<RankByEventId>({})
  const eventsById = shallowRef(new Map<HistoryMessageItem['eventId'], HistoryMessageItem>())
  const eventsByForwardingEventId = shallowRef(new Map<HistoryMessageItem['eventId'], HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>>())
  const deletedEventIds = ref(new Map<PServed, HistoryMessageItem['eventId']>())
  const deletedEventsByEventId = shallowRef(new Map<HistoryMessageItem['eventId'], HistoryMessageItem>())
  const eventIdsByQuotedEventId = shallowRef(new Map<HistoryMessageItem['eventId'], Set<HistoryMessageItem['eventId']>>())
  const eventsByRoomPServedAndRank = ref<EventByRoomPServedAndRank>({})

  const indexDeletedEvent = (event: HistoryMessageItem) => {
    deletedEventsByEventId.value.set(event.eventId, event)
  }

  const $reset = () => {
    eventsByRoomPServed.value = {}
    roomIdByEventId.value = new Map()
    rankByEventId.value = {}
    eventsById.value = new Map()
    eventsByForwardingEventId.value = new Map()
    deletedEventsByEventId.value = new Map()
    eventIdsByQuotedEventId.value = new Map()
    eventsByRoomPServedAndRank.value = {}

    _triggerStorageRef()
  }

  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()
  const {
    handleDeletedMessage,
    getOwnMessageRankByRoomAndContactId,
    getMarkedUnread,
    updateFromContactStatus,
    updateLastReadEventId,
  } = useStoreLastReadEventIds()

  let _chatStoreIsReadyResolveFn: (value: boolean) => void
  const chatStoreIsReady: Promise<boolean> = new Promise((resolve) => {
    _chatStoreIsReadyResolveFn = resolve
  })

  const pendingForApplyMessagesByEventId: Map<number, HistoryMessageItem<
    MessageUpdatedByOtherEvent |
    MessageUpdatedByYouEvent |
    MessageDeletedByOtherEvent |
    MessageDeletedByYouEvent |
    MessageMarkedAsRead
  >> = new Map()

  const findRoomMessages = (roomPServed: PServed): Array<HistoryMessageItem> => {
    const events = eventsByRoomPServed.value[roomPServed]
    return events?.filter((message) => {
      if (isMessageDeleted(message)) {
        return false
      }

      let appointmentEnded = true
      const typedItem = message.item as MessageCallAppointmentStart
      if (typedItem.event === 'appointment_start') {
        appointmentEnded = !!events.find(e => {
          const typedEndedItem = e.item as MessageCallAppointmentEnd
          return typedEndedItem.event === 'appointment_end' && typedEndedItem.confId === typedItem.confId
        })
      }

      return message.item.type !== 'MessageCallReceived' ||
        isCallPastMessage(message) ||
        !appointmentEnded
    }) ?? []
  }

  const findById = <T extends BaseEvent = BaseEvent>(id: HistoryMessageItem['eventId']) => {
    return <HistoryMessageItem<T> | undefined>eventsById.value.get(id)
  }

  const isRecordCompletedEvent = (message: HistoryMessageItem) => {
    const typedMessageRecordCompletedItem = message.item as MessageCallRecordCompleted
    return typedMessageRecordCompletedItem.event === 'call_record_completed'
  }

  const findLastMessage = (roomPServed: PServed): HistoryMessageItem | undefined => {
    return findRoomMessages(roomPServed).filter(e => !isRecordCompletedEvent(e))?.at(-1)
  }

  const findLastReadableMessageAtOrBefore = (
    roomPServed: PServed,
    eventId: HistoryMessageItem['eventId'],
  ): HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined => {
    return findRoomMessages(roomPServed)
      .filter((event): event is HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> => {
        return (event.item.type === 'IncomingMessageEvent' || event.item.type === 'OutgoingMessageEvent')
          && event.eventId <= eventId
      })
      .at(-1)
  }

  const getUnreadCount = (pServed: PServed): number => {
    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed)
    const room = useStoreRooms().findByPServed(pServed)
    if (lastReadMessageRank === undefined || !room?.isEnabled) {
      return 0
    }

    const markedUnread = getMarkedUnread(pServed)
    if (markedUnread) {
      return findRoomMessages(pServed).filter(event => {
        if (isRecordCompletedEvent(event)) {
          return false
        }

        const isAppropriateType = event.item.type !== 'OutgoingMessageEvent'
        const rankIsMoreThanLastRead = !!event.rank && event.rank >= markedUnread.rank
        return isAppropriateType && rankIsMoreThanLastRead
      })?.length ?? 0
    }

    const unread = findRoomMessages(pServed).filter(event => {
      if (isRecordCompletedEvent(event)) {
        return false
      }

      const isAppropriateType = event.item.type !== 'OutgoingMessageEvent'
      const rankIsMoreThanLastRead = !!event.rank && event.rank > lastReadMessageRank
      return isAppropriateType && rankIsMoreThanLastRead
    })?.length ?? 0
    return unread
  }

  const totalUnreadCount = computed(() => {
    return Object.keys(eventsByRoomPServed.value)
      .reduce(
        (acc, pServed) => acc + (getUnreadCount(pServed) > 0 ? 1 : 0),
        0,
      )
  })

  const _triggerStorageRef = () => {
    triggerRef(eventsById)
    triggerRef(roomIdByEventId)
    triggerRef(eventsByRoomPServed)
    triggerRef(rankByEventId)
    triggerRef(eventsByForwardingEventId)
    triggerRef(eventIdsByQuotedEventId)
  }

  const removeEventFromQuotedIndex = (
    eventId: HistoryMessageItem['eventId'],
    event?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    const sourceEvent = event ?? (eventsById.value.get(eventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined)
    const quotedEventId = sourceEvent?.item.quote?.quotedEventId
    if (!quotedEventId) {
      return
    }

    const quotedEventIds = eventIdsByQuotedEventId.value.get(quotedEventId)
    quotedEventIds?.delete(eventId)
    if (!quotedEventIds?.size) {
      eventIdsByQuotedEventId.value.delete(quotedEventId)
    }
  }

  const addEventToQuotedIndex = (
    eventId: HistoryMessageItem['eventId'],
    event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    const quotedEventId = event.item.quote?.quotedEventId
    if (!quotedEventId) {
      return
    }

    if (!eventIdsByQuotedEventId.value.has(quotedEventId)) {
      eventIdsByQuotedEventId.value.set(quotedEventId, new Set([eventId]))
      return
    }

    eventIdsByQuotedEventId.value.get(quotedEventId)?.add(eventId)
  }

  const removeEventFromForwardingIndex = (
    event?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    const forwardingEventId = event?.item.forwarding?.forwardingEventId
    if (!forwardingEventId) {
      return
    }

    eventsByForwardingEventId.value.delete(forwardingEventId)
  }

  const addMessage = (pServed: string, message: HistoryMessageItem) => {
    _addMessageWithoutTriggerRef(pServed, message)
    _triggerStorageRef()
  }

  /**
    * Приватный метод не вызывающий триггер реактивности стора
    *
    * @param pServed
    * @param message
    * @returns
  */
  const _addMessageWithoutTriggerRef = (pServed: string, message: HistoryMessageItem) => {
    const { eventId, rank, item } = message

    if (!eventId || !pServed || !rank) return

    const clonedMessage = { ...message, item: { ...item }} as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
    if (clonedMessage.item.files?.length) {
      clonedMessage.item.files = [...clonedMessage.item.files]
    }
    if (clonedMessage.item.mentionedUsers) {
      clonedMessage.item.mentionedUsers = [...clonedMessage.item.mentionedUsers]
    }

    eventsById.value.set(eventId, clonedMessage)

    const forwardingEventId = clonedMessage.item.forwarding?.forwardingEventId
    if (forwardingEventId) {
      eventsByForwardingEventId.value.set(forwardingEventId, clonedMessage)
    }

    roomIdByEventId.value.set(eventId, pServed)

    if (!eventsByRoomPServed.value[pServed]) {
      eventsByRoomPServed.value[pServed] = []
    }
    if (!eventsByRoomPServedAndRank.value[pServed]) {
      eventsByRoomPServedAndRank.value[pServed] = {}
    }

    insertAtStore<HistoryMessageItem, 'rank'>(eventsByRoomPServed.value[pServed], clonedMessage, 'rank')
    roomIdByEventId.value.set(eventId, pServed)
    rankByEventId.value[eventId] = rank
    eventsById.value.set(eventId, clonedMessage)

    if (isMessageDeleted(message)) {
      // Tombstone из dialogs/history: сохраняем cursor, чтобы prefetch мог догрузить более старые видимые сообщения
      saveDeletedEventId(pServed, eventId)
      indexDeletedEvent(clonedMessage)
    } else {
      eventsByRoomPServedAndRank.value[pServed][rank] = clonedMessage
    }

    if (forwardingEventId) {
      eventsByForwardingEventId.value.set(forwardingEventId, clonedMessage)
    }

    const update = pendingForApplyMessagesByEventId.get(eventId)
    if (update) {
      if (update.item.type === 'UpdateMessageEvent') {
        updateMessageText(update as HistoryMessageItem<MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent>)
      } else if (update.item.type === 'DeleteMessageEvent') {
        _deleteMessage(eventId, update.item.pServed)
      } else if (update.item.type === 'MessageMarkedAsRead') {
        updateMessageStatus(eventId, update.item.pServed, update.item.executor, update.status, update.rank)
      }
      pendingForApplyMessagesByEventId.delete(eventId)
    }

    addEventToQuotedIndex(eventId, clonedMessage)
  }

  const updateMessage = (pServed: PServed, payload: HistoryMessageItem, oldEventId: number, oldRank?: number) => {
    _updateMessageWithoutTriggerRef(pServed, payload, oldEventId)
    _triggerStorageRef()

    if (oldRank) {
      delete eventsByRoomPServedAndRank.value[pServed]?.[oldRank]
    }
  }

  /**
   * Приватный метод не вызывающий триггер реактивности стора
   *
   * @param pServed
   * @param payload
   * @param oldEventId
   * @returns
   */
  const _updateMessageWithoutTriggerRef = (pServed: PServed, payload: HistoryMessageItem, oldEventId: number) => {
    const { rank, eventId } = payload
    if (!eventId) return
    const oldEvent = eventsById.value.get(oldEventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
    eventsById.value.set(eventId, payload)

    const forwardingEventId = (payload.item as MessageIncomingEvent | MessageOutgoingEvent).forwarding?.forwardingEventId
    if (forwardingEventId) {
      eventsByForwardingEventId.value.set(forwardingEventId, (payload as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>))
    }

    if (rank) {
      rankByEventId.value[eventId] = rank
      eventsByRoomPServedAndRank.value[pServed]![rank] = payload
    } else {
      console.warn(`${pServed} No rank detected during update message`, payload)
    }
    roomIdByEventId.value.set(eventId, pServed)

    const roomMessages = eventsByRoomPServed.value[pServed]
    if (!roomMessages) return

    const index = roomMessages.findIndex((event: HistoryMessageItem) => event.eventId === oldEventId)
    if (index > -1) {
      roomMessages.splice(index, 1)
    } else {
      console.warn(`${pServed} No event detected during update message`, payload)
    }
    insertAtStore<HistoryMessageItem, 'rank'>(roomMessages, payload, 'rank')

    if (eventId !== oldEventId) {
      roomIdByEventId.value.delete(oldEventId)
      eventsById.value.delete(oldEventId)
      delete rankByEventId.value[oldEventId]
    }

    if (oldEvent) {
      removeEventFromQuotedIndex(oldEventId, oldEvent)
      removeEventFromForwardingIndex(oldEvent)
    }

    addEventToQuotedIndex(eventId, payload as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
  }

  const saveDeletedEventId = (pServed: PServed, eventId: HistoryMessageItem['eventId']) => {
    const id = deletedEventIds.value.get(pServed)
    if (!id) {
      deletedEventIds.value.set(pServed, eventId)
    } else if (eventId > id) {
      deletedEventIds.value.set(pServed, eventId)
    }
  }

  const _deleteMessage = (eventId: number, pServed?: string) => {
    if (eventIdsByQuotedEventId.value.has(eventId)) {
      eventIdsByQuotedEventId.value.delete(eventId)
    } else {
      removeEventFromQuotedIndex(eventId)
    }

    if (!pServed) {
      pServed = roomIdByEventId.value.get(eventId)
    }
    if (pServed) {
      saveDeletedEventId(pServed, eventId)
      const eventForDelete = eventsById.value.get(eventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
      removeEventFromForwardingIndex(eventForDelete)
      eventsById.value.delete(eventId)
      _deleteForwardingMessage(eventId)

      const index = eventsByRoomPServed.value[pServed]?.findIndex((event) => event.eventId === eventId)
      if (index !== undefined && index > -1) {
        resetMessage(eventId, pServed)
        eventsByRoomPServed.value[pServed]!.splice(index, 1)
      } else {
        const messageToDelete: MessageDeletedByOtherEvent = {
          eventId,
          from: pServed,
          pServed,
          rank: 0,
          targetEventId: eventId,
          timestamp: 0,
          type: 'DeleteMessageEvent',
        }
        pendingForApplyMessagesByEventId.set(eventId, {
          eventId,
          rank: 0,
          status: MessageStatusType.DELETED,
          timestamp: 0,
          type: 0,
          item: messageToDelete,
        })
      }

      findRoomMessages(pServed).forEach((m) => {
        const typed = { ...(m as HistoryMessageItem<MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent>) }
        if (typed.item.quote?.quotedEventId === eventId) {
          typed.item = { ...typed.item, quote: {} as IMQuote }
          updateMessageText(typed)
        }
      })
    }
    _triggerStorageRef()
  }

  const resetMessage = (eventId: number, pServed?: string) => {
    if (!pServed) {
      pServed = roomIdByEventId.value.get(eventId)
    }
    if (pServed) {
      const idx = eventsByRoomPServed.value[pServed]?.findIndex((e) => e.eventId === eventId)
      if (idx !== undefined && idx >= 0) {
        const event = { ...eventsByRoomPServed.value[pServed]![idx!] } as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
        if (event) {
          event.item.messageText = ''
          event.item.files = []
          event.status = MessageStatusType.DELETED
          eventsByRoomPServed.value[pServed]![idx] = event
          useReactionsStore().deleteReactionsFromStoreByEventId(eventId)
          delete eventsByRoomPServedAndRank.value[pServed]![event.rank!]
          indexDeletedEvent(event)

          const anotherEvent = eventsById.value.get(eventId)
          if (anotherEvent) {
            anotherEvent.status = MessageStatusType.DELETED
          }

          _triggerStorageRef()
        }
      }
    }
  }

  const hideMessageForSelf = (eventId: number, pServed?: string) => {
    _deleteMessage(eventId, pServed)
  }

  const _deleteForwardingMessage = (eventId: number) => {
    const event = eventsByForwardingEventId.value.get(eventId)
    if (event && event.item.forwarding) {
      const updated = { ...event, item: { ...event.item, forwarding: {
        forwardingText: '',
        forwardingFiles: [],
      }}}
      eventsByForwardingEventId.value.set(eventId, updated)
    }
    eventsByForwardingEventId.value.delete(eventId)
    triggerRef(eventsByForwardingEventId)
  }

  const deleteMessages = (pServed: PServed) => {
    eventsByRoomPServed.value[pServed]!.forEach((event) => {
      const eventId = event.eventId
      if (eventId) {
        removeEventFromQuotedIndex(eventId, event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
        removeEventFromForwardingIndex(event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
        eventsById.value.delete(eventId)
        deletedEventsByEventId.value.delete(eventId)
        _deleteForwardingMessage(eventId)
        pendingForApplyMessagesByEventId.delete(eventId)
        roomIdByEventId.value.delete(eventId)
        delete rankByEventId.value[eventId]
      }
    })
    delete eventsByRoomPServed.value[pServed]
    _triggerStorageRef()
  }

  const updateMessageText = (payload: HistoryMessageItem<MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent>) => {
    const { item, item: { pServed, era }} = payload
    const eventId = item.targetEventId
    if (pServed) {
      const index = eventsByRoomPServed.value[pServed]?.findIndex((event) => event.eventId === eventId)
      if (index !== undefined && index >= 0) {
        const event = { ...eventsByRoomPServed.value[pServed]![index!] as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> }

        event.item.messageText = item.messageText
        event.item.files = item.files ?? []
        event.item.quote = item.quote ?? {} as IMQuote
        event.item.era = era
        eventsByRoomPServed.value[pServed]![index] = event
        eventsById.value.set(eventId, event)
        if (event.rank !== undefined && eventsByRoomPServedAndRank.value[pServed]?.[event.rank]) {
          eventsByRoomPServedAndRank.value[pServed]![event.rank] = event
        }
      } else {
        const event = pendingForApplyMessagesByEventId.get(eventId)
        if (event) {
          const eventItem = {
            ...event.item,
            messageText: item.messageText,
            files: item.files,
            forwarding: item.forwarding,
            quote: item.quote,
          }
          pendingForApplyMessagesByEventId.set(eventId, {
            ...event,
            item: eventItem as MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent,
          })
        }
      }
      _triggerStorageRef()
    }
  }

  const updateMessageStatus = (
    eventId: number,
    pServed: string,
    contactId: string,
    status: MessageStatusType,
    rank = 0,
  ) => {
    if (contactId === currentUser.value?.imLogin) {
      useNotificationItemStore().updateUnread(pServed, eventId)
    }

    const isCurrentUser = currentUser.value?.imLogin === contactId || currentUser.value?.imLogin === pServed
    if (isCurrentUser) {
      const message = findById(eventId)
      if (message) {
        const updated = { ...message, status } as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
        eventsById.value.set(eventId, updated)
        const index = eventsByRoomPServed.value[pServed]?.findIndex((e) => e.eventId === eventId)
        if (index !== undefined && index >= 0) {
          eventsByRoomPServed.value[pServed]![index] = updated
        }
        const rankMap = eventsByRoomPServedAndRank.value[pServed]
        if (rankMap && message.rank !== undefined && rankMap[message.rank]) {
          eventsByRoomPServedAndRank.value[pServed]![message.rank] = updated
        }
      } else {
        const update: MessageMarkedAsRead = {
          type: 'MessageMarkedAsRead',
          eventId,
          rank,
          timestamp: 0,
          from: pServed,
          eventIdMarkedAsRead: eventId,
          executor: contactId,
          pServed: pServed,
        }
        pendingForApplyMessagesByEventId.set(eventId, {
          eventId, status, timestamp: 0, item: update, rank, type: 0,
        })
      }
    }
    _triggerStorageRef()
  }

  const isCurrentUserMentionedOrQuotedAtUnreadMessages = (pServed: PServed) => {
    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed)
    const currentImLogin = currentUser.value?.imLogin
    return !!findRoomMessages(pServed).filter(isTextMessage).find(({ rank, item: { mentionedUsers, quote }}) => {
      if (rank <= lastReadMessageRank) {
        return false
      }
      if (!!mentionedUsers?.find((s) => s === currentImLogin)) {
        return true
      }
      if (quote?.quotedEventId) {
        const quotedMessage = findById(quote.quotedEventId)  // todo if not in history
        return quotedMessage?.item.type === 'OutgoingMessageEvent'
      }
      return false
    })
  }

  const getHistoryCursorEventId = (pServed: PServed, eventId?: number): number | undefined => {
    if (eventId) {
      return eventId
    }

    const visibleOldestEventId = findRoomMessages(pServed)[0]?.eventId
    if (visibleOldestEventId) {
      return visibleOldestEventId
    }

    // В сторе могут остаться только удалённые сообщения (tombstone) — findRoomMessages их скрывает.
    // Берём самый старый raw-event как cursor, чтобы запросить более старую видимую историю.
    const rawOldestEventId = eventsByRoomPServed.value[pServed]?.[0]?.eventId
    if (rawOldestEventId) {
      return rawOldestEventId
    }

    return deletedEventIds.value.get(pServed)
  }

  const prefetchHistory = async (pServed: PServed) => {
    await chatStoreIsReady
    const unread = getUnreadCount(pServed)
    const messagesCount = findRoomMessages(pServed).length
    const notInHistory = unread - messagesCount
    const notInRenderLimit = REQUEST_HISTORY_MESSAGES_LIMIT - messagesCount
    let messages
    if (notInHistory > 0) {
      messages = await fetchHistory(pServed, notInHistory)
    } else if (notInRenderLimit > 0) {
      messages = await fetchHistory(pServed, notInRenderLimit)
    }
    if (messages) {
      addMessages(pServed, messages)
    }
    return Promise.resolve(true)
  }

  const fetchHistory = async (pServed: string, messagesLimit: number = REQUEST_HISTORY_MESSAGES_LIMIT, eventId?: number): Promise<HistoryMessageItem[]> => {
    const { currentGuest } = useGuestAuth()

    try {
      const cursorEventId = getHistoryCursorEventId(pServed, eventId)

      const canRequest = currentGuest.value === undefined ? cursorEventId !== undefined : true

      if (canRequest) {
        const request: RequestChatHistoryPayload = {
          pServed,
          isConference: pServedIsRoom(pServed),
          cursorEventId: cursorEventId!,
          type: 'older',
          messagesLimit,
        }
        const history = await requestChatHistory(request)
        useReactionsStore().setReactionsByHistory(history)

        return Promise.resolve(history.events)
      }
      return Promise.resolve([])
    } catch (e) {
      return Promise.reject(e)
    }
  }

  const addMessages = (pServed: string, messages: HistoryMessageItem[]) => {
    messages.forEach((event: HistoryMessageItem) => _addMessageWithoutTriggerRef(pServed, event))
    _triggerStorageRef()
  }

  const cleanupOldRoomMessages = (pServed: PServed) => {
    const messages = findRoomMessages(pServed)
    const unread = getUnreadCount(pServed)
    if (messages.length < (unread + REQUEST_HISTORY_MESSAGES_LIMIT * 2)) {
      return
    }

    const toDeleteIndex = messages.length - Math.max(REQUEST_HISTORY_MESSAGES_LIMIT, unread)
    const minRankToKeep = messages[toDeleteIndex]?.rank
    if (minRankToKeep === undefined) {
      return
    }

    const rawMessages = eventsByRoomPServed.value[pServed] ?? []
    rawMessages
      .filter((message) => message.rank !== undefined && message.rank < minRankToKeep)
      .forEach(({ eventId, rank }) => {
        removeEventFromQuotedIndex(eventId)
        const eventForDelete = eventsById.value.get(eventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
        removeEventFromForwardingIndex(eventForDelete)
        eventsById.value.delete(eventId)
        deletedEventsByEventId.value.delete(eventId)
        roomIdByEventId.value.delete(eventId)
        eventsByRoomPServed.value[pServed] = eventsByRoomPServed.value[pServed]!.filter((m) => m.eventId !== eventId)
        delete rankByEventId.value[eventId]
        eventsByForwardingEventId.value.delete(eventId)
        eventIdsByQuotedEventId.value.delete(eventId)
        if (rank !== undefined) {
          delete eventsByRoomPServedAndRank.value[pServed]?.[rank]
        }
      })
    _triggerStorageRef()
  }

  const fetchAndAddHistory = async (pServed: string, messagesLimit: number = REQUEST_HISTORY_MESSAGES_LIMIT) => {
    const { startLoading, stopLoading } = useChatHistoryLoading()

    try {
      startLoading()
      const history = await fetchHistory(pServed, messagesLimit)
      if (history) {
        addMessages(pServed, history)
      }
      return history
    } catch (e) {
      return Promise.reject(e)
    } finally {
      await yieldToMainThread()
      stopLoading()
    }
  }

  const getDeletedEvent = (eventId: HistoryMessageItem['eventId']) => {
    const fromIndex = deletedEventsByEventId.value.get(eventId)
    if (fromIndex) {
      return fromIndex
    }

    const event = eventsById.value.get(eventId)
    return event?.status === MessageStatusType.DELETED ? event : undefined
  }

  /**
   * Feature «sync-im-history» (или «im-session-init») или или отдельный app-level модуль инициализации IM
   *
   * Один слой фичи, который знает про useIM(), userImIsReady, requestDialogs, onHistory и решает:
   * когда запросить диалоги, когда применить пришедшую историю к комнатам,
   * контактам и к сторам сообщений/реакций/звонков.
   * Entities по-прежнему только принимают «готовые» данные.
   */
  const { userImIsReady } = storeToRefs(useIMStore())
  watch(userImIsReady, (value) => {
    if (value) {
      requestDialogs().then(async (history: HistoryResponse) => {
        applyHistoryEvents(history)
        useReactionsStore().setReactionsByHistory(history)

        _triggerStorageRef()
        _chatStoreIsReadyResolveFn(true)
        useAppBadge().setAppIconBadge(totalUnreadCount.value)
      }).catch((e) => {
        console.error(e)
        _triggerStorageRef()
        _chatStoreIsReadyResolveFn(false)
      })
    }
  })

  watch(totalUnreadCount, (value, oldValue) => {
    const { setAppIconBadge, clearAppBadge } = useAppBadge()
    updateStatusIcon()
    if (oldValue && !value) {
      clearAppBadge().catch((e: Error) => console.error(e))
    } else if (value) {
      setAppIconBadge(value).catch((e: Error) => console.error(e))
    }
  })

  const onMessageEventReceived = (event: BaseByOtherEvent | MessageCallReceived) => {
    const callTypedEvent = event as MessageCallReceived
    if (
      event.type === 'MessageCallReceived' &&
      !isCallMessage({ item: event }) ||
      event.type === 'ConferenceRemoved' ||
      callTypedEvent.event === 'call_answered'
    ) {
      return
    }

    const { timestamp, eventId, pServed, rank, type } = event
    const modifiedEvent = { ...event }
    // Для чата Сохраненные сообщения, в котором мы пишем сами себе
    if (type === 'IncomingMessageEvent' && event.executor === currentUser?.value?.imLogin && event.pServed === currentUser.value?.imLogin) {
      modifiedEvent.type = 'OutgoingMessageEvent'
    }

    addMessage(pServed, {
      eventId, type: 0, timestamp, rank,
      status: MessageStatusType.ACCEPTED,
      item: modifiedEvent,
    })

    if (type === 'MessageCallReceived') {
      const callEvent = event as MessageCallReceived
      const isOutgoingCall =
        'srcPServed' in callEvent &&
        !!currentUser.value?.imLogin &&
        callEvent.srcPServed === currentUser.value.imLogin

      // call_ended — всегда; исходящие (недозвон/отмена с этого или другого аппарата) — сразу прочитаны
      if (callEvent.event === 'call_ended' || isOutgoingCall) {
        markAsRead({
          eventIdMarkedAsRead: eventId,
          to: pServed,
        }).then(result => {
          const userId = currentUser.value?.imLogin ?? currentGuest.value?.pServed
          if (result && userId) {
            updateLastReadEventId(pServed, userId, eventId, rank)
          }
        })
      }
    }
  }

  const getPreviousEventByRank = (pServed: PServed, rank: Rank): HistoryMessageItem | undefined => {
    let candidateRank = rank - 1
    if (candidateRank < 0) return undefined

    const eventsByPServed = eventsByRoomPServedAndRank.value[pServed]
    if (!eventsByPServed) return undefined

    while (candidateRank >= 0) {
      const candidate = eventsByPServed[candidateRank]
      if (candidate) return candidate
      candidateRank -= 1
    }
    return undefined
  }

  const deleteEventByRank = (rank: Rank, pServed: PServed) => {
    if (eventsByRoomPServedAndRank.value[pServed]) {
      delete eventsByRoomPServedAndRank.value[pServed][rank]
    }
  }

  onMessage(onMessageEventReceived)
  onConferenceCreated(onMessageEventReceived)
  onConferenceEdited(onMessageEventReceived)
  onPinMessage(onMessageEventReceived)
  onUnpinMessage(onMessageEventReceived)

  onEditMessage((event: MessageUpdatedByOtherEvent) => {
    const { timestamp, eventId, rank } = event
    const status = eventsById.value.get(eventId)?.status ?? MessageStatusType.ACCEPTED
    updateMessageText({
      eventId, type: 0, timestamp, rank,
      status,
      item: event,
    })
  })

  /**
   * Применяет удаление сообщения из IM.
   * Используется и для onDeleteMessage (удаление другим), и для HistoryResponse с DeleteMessageEvent
   * (в т.ч. удаление «собой» на другой сессии той же учётной записи).
   */
  const applyDeleteMessageEvent = (event: MessageDeletedByOtherEvent | MessageDeletedByYouEvent) => {
    const roomKey = 'from' in event ? event.from : event.pServed
    const message = eventsById.value.get(event.targetEventId)
    if (message) {
      deleteEventByRank(message.rank, roomKey)
    }

    _deleteMessage(event.targetEventId, event.pServed)
    handleDeletedMessage(roomKey, event.targetEventId)
  }

  onDeleteMessage((event: MessageDeletedByOtherEvent) => {
    applyDeleteMessageEvent(event)
  })

  const applyHistoryEvents = (history: HistoryResponse) => {
    const _contactPServedsForFetch: PServed[] = []
    const pendingGroupStatusEvents: GroupStatusEvent[] = []
    const roomsToReconcileReadMarks = new Set<PServed>()
    history.events.forEach((event) => {
      const { item: { pServed }} = event
      if (event.item.type === 'GroupStatusEvent' && pServed) {
        const roomId = fromPServedUserIfRoom(pServed)
        const contact = useStoreContacts().findByPServed(roomId)
        if (contact) {
          createPrivateRoom(roomId, contact)
        } else if (!pServedIsRoom(pServed)) {
          _contactPServedsForFetch.push(pServed)
        }
        pendingGroupStatusEvents.push(event.item as GroupStatusEvent)
      } else if (event.item.type === 'DeleteMessageEvent') {
        // Realtime-удаление через HistoryResponse (например, та же учётка во втором браузере)
        applyDeleteMessageEvent(event.item as MessageDeletedByOtherEvent | MessageDeletedByYouEvent)
      } else if (pServed) {
        const { item: { type }, eventId } = event
        const isNotMyCallMessage = isCallMessage(event) && currentUser?.value?.imLogin !== pServed
        if (isMessageNotReactionOrNotification(event) && (event.item.type !== 'MessageCallReceived' || isNotMyCallMessage || event.item.event === 'appointment_start')) {
          _addMessageWithoutTriggerRef(pServed, event)
          const currentImLogin = currentUser.value?.imLogin
          if (currentImLogin && type === 'OutgoingMessageEvent') {
            updateMessageStatus(
              eventId,
              pServed,
              currentImLogin,
              MessageStatusType.READ,
            )
          }
        }
      }
    })
    pendingGroupStatusEvents.forEach((typedEvent) => {
      if (typedEvent.extendedParams.isEnabled || !pServedIsRoom(typedEvent.pServed)) {
        useStoreRooms().updateFromContactStatus(typedEvent)
        useRoomScheduleStore().updateFromContactStatus(typedEvent)
        updateFromContactStatus(typedEvent)
        roomsToReconcileReadMarks.add(typedEvent.pServed)
      }
    })
    roomsToReconcileReadMarks.forEach((pServed) => {
      useStoreLastReadEventIds().reconcileReadMarksForRoom(pServed)
    })
    useStoreContacts().fetchContactsBySomeIds(_contactPServedsForFetch, 'imLogin', true)
      .then((contacts: Contact[] | undefined) => {
        contacts?.forEach(c => createPrivateRoom(c.imLogin, c))
      })
  }

  onHistory((history: HistoryResponse) => {
    applyHistoryEvents(history)
    _triggerStorageRef()
  })

  onMarkAsRead((event: MessageMarkedAsRead) => {
    updateMessageStatus(
      event.eventIdMarkedAsRead,
      event.pServed,
      event.executor,
      MessageStatusType.READ,
    )
  })

  return {
    chatStoreIsReady,
    eventsByRoomPServed: computed(() => eventsByRoomPServed.value),
    eventIdsByQuotedEventId: computed(() => new Map(eventIdsByQuotedEventId.value)),
    $reset,
    findRoomMessages,
    findById,
    findLastMessage,
    findLastReadableMessageAtOrBefore,
    getUnreadCount,
    totalUnreadCount,
    addMessage,
    updateMessage,
    updateMessageText,
    updateMessageStatus,
    deleteMessages,
    fetchAndAddHistory,
    fetchHistory,
    addMessages,
    prefetchHistory,
    resetMessage,
    hideMessageForSelf,
    isCurrentUserMentionedOrQuotedAtUnreadMessages,
    getPreviousEventByRank,
    cleanupOldRoomMessages,
    getDeletedEvent,
  }
})
